import React, {
  createContext,
  type PropsWithChildren,
  useContext,
  useMemo,
  useState,
} from "react";
import type { RoleName } from "common/authorization";
import { AuthContext } from "../../../../Authorization/AuthContext";
import type { ExistingSubmissionType } from "./types";

export type CanCreateGroup = {
  id: string;
  name: string;
  memberType: string;
};

export enum SUBMISSIONS_BUILDER_MODE {
  BUILD = "BUILD",
  VERSION_HISTORY = "VERSION_HISTORY",
}

export interface SubmissionsBuilderContextValue {
  canCreateRoles: Array<RoleName>;
  setCanCreateRoles: (roles: Array<RoleName>) => void;
  canCreateGroups: Array<CanCreateGroup>;
  setCanCreateGroups: (groups: Array<CanCreateGroup>) => void;
  mode: SUBMISSIONS_BUILDER_MODE;
  setMode: (mode: SUBMISSIONS_BUILDER_MODE) => void;
}

const SubmissionsBuilderContext = createContext<
  SubmissionsBuilderContextValue | undefined
>(undefined);

type SubmissionsBuilderContextProviderProps = PropsWithChildren<{
  existingSubmissionType?: Maybe<ExistingSubmissionType>;
}>;

export function SubmissionsBuilderContextProvider({
  existingSubmissionType,
  children,
}: SubmissionsBuilderContextProviderProps) {
  const { account } = useContext(AuthContext);

  const accountUserGroups = account?.userGroups ?? [];
  const initialCanCreateGroups = accountUserGroups.filter(userGroup =>
    existingSubmissionType?.canCreateGroups.find(
      canCreateGroup => canCreateGroup.id === userGroup.id,
    ),
  );
  const [canCreateGroups, setCanCreateGroups] = useState<Array<CanCreateGroup>>(
    initialCanCreateGroups,
  );

  const [canCreateRoles, setCanCreateRoles] = useState<Array<RoleName>>(
    existingSubmissionType?.canCreate ?? [],
  );

  const [mode, setMode] = useState<SUBMISSIONS_BUILDER_MODE>(
    SUBMISSIONS_BUILDER_MODE.BUILD,
  );

  const value: SubmissionsBuilderContextValue = useMemo(
    () => ({
      canCreateRoles,
      setCanCreateRoles,
      canCreateGroups,
      setCanCreateGroups,
      mode,
      setMode,
    }),
    [
      canCreateRoles,
      canCreateGroups,
      setCanCreateRoles,
      setCanCreateGroups,
      mode,
      setMode,
    ],
  );

  return (
    <SubmissionsBuilderContext.Provider value={value}>
      {children}
    </SubmissionsBuilderContext.Provider>
  );
}

export function useSubmissionsBuilderContext() {
  const context = useContext(SubmissionsBuilderContext);

  if (!context) {
    throw new Error(
      "useSubmissionsBuilderContext must be used within a SubmissionsBuilderContextProvider",
    );
  }
  return context;
}

export function useSubmissionsBuilderMode() {
  const { mode, setMode } = useSubmissionsBuilderContext();

  return useMemo(() => [mode, setMode] as const, [mode, setMode]);
}
